import type { ConsortiumCategory, ConsortiumSlug, FaqItem } from '@/types'

export const consortiumCategories: ConsortiumCategory[] = [
    {
        slug: 'imoveis',
        name: 'Imóveis',
        title: 'Consórcio de imóveis',
        tagline: 'Casa, apartamento, terreno, construção ou reforma.',
        headline: 'A casa própria planejada,',
        headlineAccent: 'sem pagar juros.',
        description: 'Compre o imóvel novo ou usado, o terreno ou a construção com uma carta de crédito à vista. Você pode usar o FGTS e paga só a taxa de administração, diluída nas parcelas.',
        seoDescription: 'Consórcio de imóveis sem juros: créditos de R$ 100 mil a R$ 1 milhão, prazos de até 200 meses e uso do FGTS. Simule sua parcela na hora.',
        credit: {
            min: 100000,
            max: 1000000,
            step: 10000,
            initial: 300000
        },
        terms: [120, 150, 180, 200],
        initialTerm: 180,
        adminFee: 0.17,
        reserveFund: 0.02,
        financing: {
            label: 'financiamento imobiliário',
            monthlyRate: 0.0095
        },
        adjustmentIndex: 'INCC ou IPCA',
        uses: [
            {
                title: 'Imóvel novo ou usado',
                description: 'Casa ou apartamento pronto para morar, na planta ou em construção.'
            },
            {
                title: 'Terreno',
                description: 'Compre o lote para construir agora ou mais adiante.'
            },
            {
                title: 'Construção e reforma',
                description: 'Use o crédito para construir, ampliar ou reformar o seu imóvel.'
            },
            {
                title: 'Imóvel comercial',
                description: 'Sala, loja ou galpão para o seu negócio ou para gerar renda com aluguel.'
            },
            {
                title: 'Imóvel rural',
                description: 'Chácara, sítio ou fazenda, conforme as regras da administradora.'
            },
            {
                title: 'Quitar financiamento',
                description: 'Troque juros por taxa de administração quitando um financiamento imobiliário.'
            }
        ],
        highlights: [
            {
                title: 'FGTS no consórcio',
                description: 'Use o saldo para ofertar lance, complementar a carta de crédito ou amortizar parcelas, seguindo as regras do FGTS.'
            },
            {
                title: 'Crédito corrigido todo ano',
                description: 'A carta de crédito é reajustada junto com as parcelas pelo índice do contrato e acompanha o preço dos imóveis.'
            },
            {
                title: 'Poder de compra à vista',
                description: 'Com o crédito liberado, você negocia como quem paga à vista e pode conseguir um preço melhor.'
            }
        ],
        faq: [
            {
                question: 'Posso usar o FGTS no consórcio de imóveis?',
                answer: 'Sim. Para imóvel residencial, o FGTS pode ser usado para ofertar lance, complementar a carta de crédito ou amortizar o saldo devedor, desde que você e o imóvel atendam às regras do fundo.'
            },
            {
                question: 'Posso comprar um imóvel usado ou na planta?',
                answer: 'Sim. A carta de crédito vale para imóveis novos, usados, na planta ou em construção. Antes da liberação, a administradora avalia o imóvel e analisa a documentação.'
            },
            {
                question: 'O imóvel fica no meu nome?',
                answer: 'Fica. O imóvel é registrado no seu nome e permanece alienado à administradora como garantia até a quitação das parcelas, como acontece no financiamento.'
            },
            {
                question: 'Posso usar o crédito para construir?',
                answer: 'Pode. Na construção, o valor é liberado em etapas, de acordo com o andamento da obra e as regras da administradora.'
            }
        ]
    },
    {
        slug: 'automoveis',
        name: 'Automóveis',
        title: 'Consórcio de automóveis',
        tagline: 'Carro 0 km ou seminovo, para trocar ou para trabalhar.',
        headline: 'O carro novo na garagem,',
        headlineAccent: 'sem pagar juros.',
        description: 'Planeje a troca do carro com parcelas menores que as de um financiamento. Quando for contemplado, você compra à vista, 0 km ou seminovo, na loja ou de particular.',
        seoDescription: 'Consórcio de automóveis sem juros: créditos de R$ 40 mil a R$ 300 mil e prazos de até 80 meses. Simule sua parcela e receba uma proposta.',
        credit: {
            min: 40000,
            max: 300000,
            step: 5000,
            initial: 90000
        },
        terms: [36, 48, 60, 72, 80],
        initialTerm: 60,
        adminFee: 0.14,
        reserveFund: 0.02,
        financing: {
            label: 'financiamento de veículos',
            monthlyRate: 0.0179
        },
        adjustmentIndex: 'preço do bem ou IPCA',
        uses: [
            {
                title: 'Carro 0 km',
                description: 'Qualquer marca e modelo, comprado à vista na concessionária.'
            },
            {
                title: 'Seminovo',
                description: 'Veículos usados dentro da idade máxima aceita pela administradora.'
            },
            {
                title: 'Utilitário e picape',
                description: 'Para o dia a dia ou para o trabalho, com o mesmo crédito.'
            },
            {
                title: 'Troca de carro',
                description: 'Venda o atual, some ao crédito e suba de categoria.'
            },
            {
                title: 'Carro para aplicativo',
                description: 'Planeje o veículo de trabalho com uma parcela que cabe no orçamento.'
            },
            {
                title: 'Quitar financiamento',
                description: 'Use a carta de crédito para quitar o financiamento do seu carro atual.'
            }
        ],
        highlights: [
            {
                title: 'Compra à vista',
                description: 'Na hora de negociar, a carta de crédito funciona como dinheiro na mão.'
            },
            {
                title: 'Crédito atualizado',
                description: 'O valor do crédito é reajustado conforme o contrato e acompanha o preço dos carros.'
            },
            {
                title: 'Sem entrada',
                description: 'Você não precisa juntar uma entrada para começar: paga só as parcelas mensais.'
            }
        ],
        faq: [
            {
                question: 'Posso comprar um carro usado?',
                answer: 'Sim. Seminovos podem ser comprados com a carta de crédito, desde que respeitem a idade máxima e as demais regras da administradora.'
            },
            {
                question: 'Posso escolher outro modelo depois de contemplado?',
                answer: 'Pode. A carta de crédito não fica presa a um modelo. Se o carro custar mais, você completa a diferença. Se custar menos, o saldo pode abater parcelas, conforme o contrato.'
            },
            {
                question: 'Preciso dar entrada?',
                answer: 'Não. No consórcio não existe entrada: você começa pagando apenas as parcelas mensais.'
            },
            {
                question: 'O carro fica no meu nome?',
                answer: 'Sim. O veículo é registrado no seu nome, com alienação fiduciária à administradora até o fim do pagamento.'
            }
        ]
    },
    {
        slug: 'motos',
        name: 'Motos',
        title: 'Consórcio de motos',
        tagline: 'Para o dia a dia, para trabalhar ou para viajar.',
        headline: 'Sua moto nova,',
        headlineAccent: 'com parcela leve.',
        description: 'Do modelo urbano à moto de viagem, o consórcio cabe no bolso e não cobra juros. Ideal para quem usa a moto no trabalho e quer fugir das taxas do financiamento.',
        seoDescription: 'Consórcio de motos sem juros: créditos de R$ 15 mil a R$ 80 mil, prazos de até 60 meses e parcelas que cabem no bolso. Simule agora.',
        credit: {
            min: 15000,
            max: 80000,
            step: 1000,
            initial: 25000
        },
        terms: [24, 36, 48, 60],
        initialTerm: 48,
        adminFee: 0.16,
        reserveFund: 0.02,
        financing: {
            label: 'financiamento de motos',
            monthlyRate: 0.0229
        },
        adjustmentIndex: 'preço do bem ou IPCA',
        uses: [
            {
                title: 'Moto 0 km',
                description: 'Modelos urbanos, trail, custom ou esportivos, de qualquer marca.'
            },
            {
                title: 'Moto seminova',
                description: 'Usadas dentro da idade máxima aceita pela administradora.'
            },
            {
                title: 'Moto para trabalho',
                description: 'Entregas e serviços com um custo total bem menor que o do financiamento.'
            },
            {
                title: 'Scooter e elétrica',
                description: 'Opções econômicas para se deslocar pela cidade.'
            },
            {
                title: 'Moto de viagem',
                description: 'Aquela big trail ou touring planejada sem pressa e sem juros.'
            },
            {
                title: 'Troca de moto',
                description: 'Use a moto atual na negociação e suba de cilindrada.'
            }
        ],
        highlights: [
            {
                title: 'Parcela que cabe no bolso',
                description: 'Com prazos de até 60 meses, a parcela fica leve para quem está começando.'
            },
            {
                title: 'Sem juros',
                description: 'O custo é só a taxa de administração, diluída ao longo do plano.'
            },
            {
                title: 'Compra à vista',
                description: 'Negocie desconto como quem paga à vista na concessionária.'
            }
        ],
        faq: [
            {
                question: 'Posso comprar moto usada?',
                answer: 'Sim, desde que ela respeite a idade máxima e as regras da administradora para veículos usados.'
            },
            {
                question: 'Posso usar a moto para trabalhar?',
                answer: 'Pode. O uso é livre, inclusive para entregas e aplicativos. A moto fica alienada à administradora até a quitação.'
            },
            {
                question: 'Posso quitar antes do fim do plano?',
                answer: 'Sim. Você pode antecipar parcelas ou quitar o saldo devedor quando quiser, conforme as regras do grupo.'
            },
            {
                question: 'Quanto tempo leva para ser contemplado?',
                answer: 'Não existe data garantida: depende do sorteio e dos lances de cada assembleia. Ofertar um lance aumenta as suas chances de antecipar a contemplação.'
            }
        ]
    },
    {
        slug: 'pesados',
        name: 'Pesados',
        title: 'Consórcio de pesados',
        tagline: 'Caminhões, ônibus, tratores e máquinas.',
        headline: 'Mais força para o seu negócio,',
        headlineAccent: 'sem pagar juros.',
        description: 'Renove a frota, troque o caminhão ou compre máquinas agrícolas com uma carta de crédito à vista. Um jeito planejado de investir no negócio sem comprometer o caixa com juros.',
        seoDescription: 'Consórcio de caminhões, ônibus, tratores e máquinas sem juros: créditos de R$ 150 mil a R$ 1,2 milhão e prazos de até 120 meses. Simule.',
        credit: {
            min: 150000,
            max: 1200000,
            step: 10000,
            initial: 400000
        },
        terms: [60, 80, 100, 120],
        initialTerm: 100,
        adminFee: 0.13,
        reserveFund: 0.02,
        financing: {
            label: 'financiamento de pesados',
            monthlyRate: 0.0149
        },
        adjustmentIndex: 'preço do bem ou IPCA',
        uses: [
            {
                title: 'Caminhões',
                description: 'Leves, médios ou pesados, 0 km ou seminovos.'
            },
            {
                title: 'Implementos rodoviários',
                description: 'Carretas, baús, graneleiros e outros implementos.'
            },
            {
                title: 'Ônibus e vans',
                description: 'Para transporte de passageiros, fretamento ou transporte escolar.'
            },
            {
                title: 'Tratores e colheitadeiras',
                description: 'Máquinas para modernizar a produção no campo.'
            },
            {
                title: 'Máquinas de construção',
                description: 'Escavadeiras, retroescavadeiras e outros equipamentos de obra.'
            },
            {
                title: 'Renovação de frota',
                description: 'Planeje a troca de vários veículos com mais de uma cota.'
            }
        ],
        highlights: [
            {
                title: 'Pessoa física ou empresa',
                description: 'Contrate no seu CPF ou no CNPJ da empresa, com a análise de crédito feita na contemplação.'
            },
            {
                title: 'Caixa preservado',
                description: 'Sem juros e sem entrada, o investimento entra no planejamento sem pesar no fluxo de caixa.'
            },
            {
                title: 'Estratégia de lance',
                description: 'Empresas costumam usar lances para antecipar a contemplação no momento certo do negócio.'
            }
        ],
        faq: [
            {
                question: 'Empresas podem participar?',
                answer: 'Sim. O consórcio pode ser contratado por pessoa física ou jurídica, e a empresa pode ter várias cotas para planejar a renovação da frota.'
            },
            {
                question: 'Posso comprar máquinas agrícolas?',
                answer: 'Pode. Tratores, colheitadeiras e implementos agrícolas novos ou usados podem ser comprados com a carta de crédito, conforme as regras da administradora.'
            },
            {
                question: 'Posso comprar caminhão usado?',
                answer: 'Sim, respeitando a idade máxima do veículo e a avaliação feita pela administradora.'
            },
            {
                question: 'Como funciona a garantia?',
                answer: 'O próprio bem comprado fica alienado à administradora até a quitação. Em alguns casos, ela pode pedir garantias complementares na contemplação.'
            }
        ]
    },
    {
        slug: 'servicos',
        name: 'Serviços',
        title: 'Consórcio de serviços',
        tagline: 'Reforma, viagem, festa, estudos e saúde.',
        headline: 'Seus projetos',
        headlineAccent: 'pagos à vista.',
        description: 'Reforma da casa, viagem dos sonhos, casamento, faculdade ou procedimentos de saúde e estética. Com o consórcio de serviços, você planeja e paga o prestador à vista, sem juros.',
        seoDescription: 'Consórcio de serviços sem juros para reforma, viagem, festas, educação e saúde. Créditos de R$ 10 mil a R$ 60 mil. Simule sua parcela.',
        credit: {
            min: 10000,
            max: 60000,
            step: 1000,
            initial: 20000
        },
        terms: [24, 30, 36, 40],
        initialTerm: 36,
        adminFee: 0.18,
        reserveFund: 0.02,
        financing: {
            label: 'crédito pessoal',
            monthlyRate: 0.035
        },
        adjustmentIndex: 'IPCA',
        uses: [
            {
                title: 'Reforma e decoração',
                description: 'Projeto, mão de obra e materiais para renovar a casa.'
            },
            {
                title: 'Viagens',
                description: 'Pacotes, passagens e hospedagem pagos à vista.'
            },
            {
                title: 'Festas e casamento',
                description: 'Buffet, espaço, fotografia e tudo o que o grande dia pede.'
            },
            {
                title: 'Educação',
                description: 'Faculdade, pós-graduação, intercâmbio e cursos.'
            },
            {
                title: 'Saúde e estética',
                description: 'Cirurgias, tratamentos odontológicos e procedimentos estéticos.'
            },
            {
                title: 'Outros serviços',
                description: 'Serviços prestados por empresas com CNPJ, conforme as regras da administradora.'
            }
        ],
        highlights: [
            {
                title: 'Pagamento direto ao prestador',
                description: 'O crédito é pago à empresa contratada, com nota fiscal, e você negocia como cliente à vista.'
            },
            {
                title: 'Prazos curtos',
                description: 'Planos de 24 a 40 meses, ideais para projetos de médio prazo.'
            },
            {
                title: 'Sem juros',
                description: 'Uma alternativa planejada ao crédito pessoal e ao parcelamento no cartão.'
            }
        ],
        faq: [
            {
                question: 'Quais serviços posso pagar com o consórcio?',
                answer: 'Reformas, viagens, festas, educação, saúde, estética e outros serviços prestados por empresas com CNPJ. A lista exata segue as regras da administradora.'
            },
            {
                question: 'Como o dinheiro é liberado?',
                answer: 'Depois da contemplação, a administradora paga diretamente ao prestador do serviço, mediante nota fiscal ou contrato.'
            },
            {
                question: 'Posso usar o crédito em mais de um serviço?',
                answer: 'Em muitos grupos, sim: o crédito pode ser dividido entre prestadores diferentes, conforme as regras da administradora.'
            },
            {
                question: 'É melhor que o crédito pessoal?',
                answer: 'Para quem pode planejar, costuma ser bem mais barato: em vez de juros todo mês, você paga só a taxa de administração diluída no prazo.'
            }
        ]
    }
]

export const consortiumSlugs: ConsortiumSlug[] = consortiumCategories.map(category => category.slug)

export const getConsortiumCategory = (slug: string): ConsortiumCategory | undefined => {
    return consortiumCategories.find(category => category.slug === slug)
}

export const generalFaq: FaqItem[] = [
    {
        question: 'O que é consórcio?',
        answer: 'É uma forma de compra planejada em grupo. Pessoas com o mesmo objetivo pagam parcelas mensais para um fundo comum e, todo mês, participantes são contemplados com a carta de crédito para comprar o bem ou o serviço à vista.'
    },
    {
        question: 'O consórcio tem juros?',
        answer: 'Não. Em vez de juros, você paga uma taxa de administração diluída nas parcelas, além do fundo de reserva e do seguro, quando houver. Por isso o custo total costuma ser bem menor que o de um financiamento.'
    },
    {
        question: 'Como funciona o sorteio?',
        answer: 'Todo mês a administradora realiza uma assembleia. Quem está com as parcelas em dia concorre ao sorteio, e os sorteados recebem a carta de crédito.'
    },
    {
        question: 'O que é lance?',
        answer: 'É a oferta de antecipar parte das parcelas para ser contemplado antes. Os maiores lances da assembleia são contemplados. Você pode usar recursos próprios, o FGTS no caso de imóveis, ou parte da própria carta de crédito, no lance embutido.'
    },
    {
        question: 'Em quanto tempo serei contemplado?',
        answer: 'Não existe data garantida: depende do sorteio e dos lances de cada assembleia. Uma estratégia de lance bem planejada aumenta muito as suas chances de antecipar a contemplação.'
    },
    {
        question: 'As parcelas mudam com o tempo?',
        answer: 'Sim. Parcelas e carta de crédito são reajustadas uma vez por ano pelo índice previsto no contrato, como o INCC ou o IPCA. Assim, o seu crédito acompanha a valorização do bem.'
    },
    {
        question: 'Preciso comprovar renda?',
        answer: 'Para entrar no grupo, a análise é simples. A comprovação de renda é pedida na contemplação, antes da liberação do crédito, para confirmar que as parcelas restantes cabem no seu orçamento.'
    },
    {
        question: 'E se eu precisar desistir?',
        answer: 'Você pode sair do grupo. Os valores pagos, descontadas as taxas previstas em contrato, são devolvidos quando a sua cota for sorteada ou no encerramento do grupo, conforme a Lei 11.795/2008.'
    }
]
